import { useState } from 'react';
import { Send, TriangleAlert } from 'lucide-react';
import { formatCount } from '@/utils/number';
import { PCMP_ACCOUNTS_ACTION_COPY } from '../../constants/pcmpAccounts.constants';
import { useDisbursePcmpAccountsBatch } from '../../hooks/useDisbursePcmpAccountsBatch';
import { useFailPcmpAccountsBatch } from '../../hooks/useFailPcmpAccountsBatch';
import type { PcmpAccountsBatchSelection } from '../../types/pcmpAccounts.types';
import type { PcmpSkippedProposal } from '../../types/pcmpQueue.types';
import { PcmpBulkCommentDialog, type PcmpBulkOutcome } from '../PcmpBatch/PcmpBulkCommentDialog';

export type PcmpAccountsBulkAction = 'DISBURSE' | 'FAIL';

export interface PcmpAccountsBulkTarget {
  action: PcmpAccountsBulkAction;
  batchNo: number;
  count: number;
  amount: number;
  /** What the selection covers, e.g. "Selected proposals". */
  scope: string;
  selection: PcmpAccountsBatchSelection;
}

interface PcmpAccountsBulkDialogProps {
  target: PcmpAccountsBulkTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  /** Called once the API has acted, so the caller can clear its selection. */
  onDone: () => void;
  onClose: () => void;
}

/**
 * Legacy AccountsDisburseBatch / AccountsDisburseSelected and the bulk
 * AccountsMarkDisbursementFailed (before payout) on a batch's pending items.
 */
export function PcmpAccountsBulkDialog({
  target,
  refNoOf,
  onDone,
  onClose,
}: PcmpAccountsBulkDialogProps) {
  const { action, batchNo, selection } = target;
  const isDisburse = action === 'DISBURSE';
  const copy = PCMP_ACCOUNTS_ACTION_COPY[action];
  const [outcome, setOutcome] = useState<PcmpBulkOutcome | null>(null);

  const finish = (done: number, skipped: PcmpSkippedProposal[]) => {
    onDone();
    if (skipped.length === 0) {
      onClose();
      return;
    }
    setOutcome({
      title: isDisburse ? 'Disbursed' : 'Marked Disbursement Failed',
      summary: `${formatCount(done, 'proposal')} ${
        isDisburse ? 'disbursed' : 'marked Disbursement Failed'
      }. The ones below changed since the list was loaded and were left out.`,
      skipped,
    });
  };
  const disburse = useDisbursePcmpAccountsBatch(batchNo, (result) =>
    finish(result.disbursed.length, result.skipped),
  );
  const fail = useFailPcmpAccountsBatch(batchNo, (result) =>
    finish(result.failed.length, result.skipped),
  );
  const { isPending, error } = isDisburse ? disburse : fail;

  return (
    <PcmpBulkCommentDialog
      title={isDisburse ? `Disburse from batch #${batchNo}` : copy.title}
      description={isDisburse ? 'Paid in place, under the same batch number' : 'Before payout'}
      icon={isDisburse ? Send : TriangleAlert}
      submitLabel={copy.submit}
      pendingLabel={copy.pending}
      submitVariant={isDisburse ? 'default' : 'destructive'}
      scope={target.scope}
      count={target.count}
      amount={target.amount}
      note={
        isDisburse
          ? 'Each proposal is recorded as paid its proposed amount. Proposals that changed meanwhile (held, failed, already paid) are left out and listed.'
          : 'Nothing is paid; the proposals go back to the chain for correction. Proposals that changed meanwhile are left out and listed.'
      }
      field={
        isDisburse
          ? { label: 'Comment', placeholder: 'Payment reference or note' }
          : { label: 'Reason', placeholder: 'Why the payment cannot be made' }
      }
      isPending={isPending}
      error={error}
      onSubmit={(text) =>
        isDisburse
          ? disburse.mutate({ ...selection, comment: text })
          : fail.mutate({ ...selection, reason: text })
      }
      outcome={outcome}
      refNoOf={refNoOf}
      onClose={onClose}
    />
  );
}
